import { Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { ClerkProvider } from '@clerk/clerk-react';
import { appendAttributionToUrl } from '@arthelper/shared/attribution';

import App from '../App';
import ContextProvider from '../context/contextProvider';
import { ClerkGuard } from './ClerkGuard';
import { LegacyTokenMigration } from './LegacyTokenMigration';

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const CLERK_DOMAIN = new URL(import.meta.env.VITE_CLIENT_URL).hostname;

const localization = {
  signUp: {
    start: {
      title: 'Start Your Free Account',
    },
  },
  signIn: {
    start: {
      title: 'Sign In',
    },
  },
};

export function Main() {
  const primaryDomain = __TOWN_SQUARE_URL__;
  // Clerk-internal satellite bounces use these URLs; append attribution so they
  // do not drop UTMs the way bare RedirectToSignUp did pre-#3260.
  const signInUrl = appendAttributionToUrl(`${primaryDomain}/signin`);
  const signUpUrl = appendAttributionToUrl(`${primaryDomain}/signup`);
  const allowedRedirectOrigins =
    import.meta.env.VITE_ALLOWED_REDIRECT_ORIGINS?.split(',').filter(Boolean) ??
    [primaryDomain, import.meta.env.VITE_CLIENT_URL].filter(Boolean);

  // Check for legacy token on initial load
  const hasLegacyToken = typeof window !== 'undefined' && localStorage.getItem('token_data') !== null;

  // Log all Clerk-related environment variables
  const clerkEnvVars = {
    CLERK_PUBLISHABLE_KEY: CLERK_PUBLISHABLE_KEY ? `${CLERK_PUBLISHABLE_KEY.substring(0, 20)}...` : 'NOT SET',
    TOWN_SQUARE_URL: __TOWN_SQUARE_URL__,
  };

  console.log('[Main] Clerk environment variables:', clerkEnvVars);
  console.log('[Main] Main component render - ClerkProvider initialization', {
    domain: CLERK_DOMAIN,
    primaryDomain,
    signInUrl,
    signUpUrl,
    pathname: window.location.pathname,
    publishableKeySet: !!CLERK_PUBLISHABLE_KEY,
    publishableKeyPrefix: CLERK_PUBLISHABLE_KEY ? CLERK_PUBLISHABLE_KEY.substring(0, 20) : 'NOT SET',
    hasLegacyToken,
  });

  // If user has legacy token, intercept and migrate before loading app
  if (hasLegacyToken) {
    return <LegacyTokenMigration />;
  }

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      signInUrl={signInUrl}
      signUpUrl={signUpUrl}
      domain={CLERK_DOMAIN}
      isSatellite
      allowedRedirectOrigins={allowedRedirectOrigins}
      localization={localization}
      appearance={{ theme: 'simple', layout: { termsPageUrl: 'https://www.arthelper.ai/legal/terms-of-service' } }}
    >
      <ClerkGuard>
        <HelmetProvider>
          <Suspense fallback={<div />}>
            <ContextProvider>
              <App />
            </ContextProvider>
          </Suspense>
        </HelmetProvider>
      </ClerkGuard>
    </ClerkProvider>
  );
}
